import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import GUI from 'lil-gui'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import halftoneVertexShader from './shaders/halftone/vertex.glsl'
import halftoneFragmentShader from './shaders/halftone/fragment.glsl'

/**
 * Loader
 */
const loaderElement = document.querySelector('.loader')
const loaderBar = document.querySelector('.loader-bar')
const loaderPercentage = document.querySelector('.loader-percentage')

let loadedAssets = 0
const totalAssets = 1 // Suzanne GLB

const updateLoader = (progress) => {
    const percentage = Math.min(Math.round(progress * 99), 99)
    loaderBar.style.width = `${percentage}%`
    loaderPercentage.textContent = `${percentage}%`
}

const finishLoading = () => {
    // Show 99% first
    loaderBar.style.width = '99%'
    loaderPercentage.textContent = '99%'

    // After 1 second, complete to 100% and hide loader
    setTimeout(() => {
        loaderBar.style.width = '100%'
        loaderPercentage.textContent = '100%'

        setTimeout(() => {
            loaderElement.classList.add('hidden')
            canvas.classList.add('loaded')

            // Show visual enhancements
            document.querySelector('.vignette').classList.add('visible')
            document.querySelectorAll('.corner-accent').forEach(el => el.classList.add('visible'))
        }, 300)
    }, 1000)
}

/**
 * Base
 */
// Debug
const gui = new GUI()

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const loadingManager = new THREE.LoadingManager()

loadingManager.onProgress = (url, loaded, total) => {
    updateLoader(loaded / total)
}

loadingManager.onLoad = () => {
    finishLoading()
}

const gltfLoader = new GLTFLoader(loadingManager)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Update material
    material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(25, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 7
camera.position.y = 7
camera.position.z = 7
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.05
controls.enablePan = false
controls.enableRotate = true
controls.minPolarAngle = Math.PI / 4
controls.maxPolarAngle = Math.PI - Math.PI / 4
controls.minDistance = 5
controls.maxDistance = 20

/**
 * Renderer
 */
const rendererParameters = {}
rendererParameters.clearColor = '#171a1c'

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    powerPreference: 'high-performance'
})
renderer.setClearColor(rendererParameters.clearColor)
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)

gui
    .addColor(rendererParameters, 'clearColor')
    .name('Background')
    .onChange(() =>
    {
        renderer.setClearColor(rendererParameters.clearColor)
    })

/**
 * Material
 */
const materialParameters = {}
materialParameters.color = '#8a8685'
materialParameters.shadowColor = '#7033ff'
materialParameters.shaderColor = '#7033ff'
materialParameters.lightColor = '#c4c4c4'

const material = new THREE.ShaderMaterial({
    vertexShader: halftoneVertexShader,
    fragmentShader: halftoneFragmentShader,
    uniforms:
    {
        uColor: new THREE.Uniform(new THREE.Color(materialParameters.color)),
        uShaderColor: new THREE.Uniform(new THREE.Color(materialParameters.shaderColor)),
        uResolution: new THREE.Uniform(new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)),
        uShadowRepititions: new THREE.Uniform(85),
        uShadowColor: new THREE.Uniform(new THREE.Color(materialParameters.shadowColor)),
        uLightRepititions: new THREE.Uniform(131),
        uLightColor: new THREE.Uniform(new THREE.Color(materialParameters.lightColor))
    }
})

gui
    .addColor(materialParameters, 'color')
    .name('Base Color')
    .onChange(() =>
    {
        material.uniforms.uColor.value.set(materialParameters.color)
    })

gui
    .add(material.uniforms.uShadowRepititions, 'value')
    .min(1)
    .max(300)
    .step(1)
    .name('Shadow Dots')


gui
    .addColor(materialParameters, 'shadowColor')
    .name('Shadow Color')
    .onChange(() =>
    {
        material.uniforms.uShadowColor.value.set(materialParameters.shadowColor)
    })

gui
    .add(material.uniforms.uLightRepititions, 'value')
    .min(1)
    .max(300)
    .step(1)
    .name('Light Dots')


gui
    .addColor(materialParameters, 'lightColor')
    .name('Light Color')
    .onChange(() =>
    {
        material.uniforms.uLightColor.value.set(materialParameters.lightColor)
    })

/**
 * Objects
 */
// Torus knot
const torusKnotGeometry = new THREE.TorusKnotGeometry(0.6, 0.25, 128, 32)
const torusKnot = new THREE.Mesh(torusKnotGeometry, material)
torusKnot.position.x = 3
torusKnot.frustumCulled = true
scene.add(torusKnot)

// Sphere
const sphereGeometry = new THREE.SphereGeometry(1, 32, 32)
const sphere = new THREE.Mesh(sphereGeometry, material)
sphere.position.x = - 3
sphere.frustumCulled = true
scene.add(sphere)

// Suzanne
let suzanne = null
gltfLoader.load(
    './suzanne.glb',
    (gltf) =>
    {
        suzanne = gltf.scene
        suzanne.traverse((child) =>
        {
            if(child.isMesh)
                child.material = material
        })
        scene.add(suzanne)
    }
)

/**
 * Animate
 */
const clock = new THREE.Clock()
let animationId = null

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    // Rotate objects
    if(suzanne)
    {
        suzanne.rotation.x = - elapsedTime * 0.1
        suzanne.rotation.y = elapsedTime * 0.2
    }

    sphere.rotation.x = - elapsedTime * 0.1
    sphere.rotation.y = elapsedTime * 0.2

    torusKnot.rotation.x = - elapsedTime * 0.1
    torusKnot.rotation.y = elapsedTime * 0.2

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    animationId = window.requestAnimationFrame(tick)
}

// Visibility change - pause when tab is hidden
document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        if (animationId) {
            cancelAnimationFrame(animationId)
            animationId = null
        }
        clock.stop()
    } else {
        clock.start()
        tick()
    }
})

tick()
